<template>
    <v-card class="d-flex flex-column" style="max-height: 100%">
        <v-data-table
            class="elevation-2"
            style="max-height: 100%; overflow-y: auto"
            :headers="headers"
            :items="usersWithAllProjects"
            @click:row="goToUserInfo"
            :hide-default-footer="true"
            :fixed-header="true"
            items-per-page="-1"
            :sort-by="sortByConfig"
        >
            <!-- Header for 'Users' column with sorting menu -->
            <template #header.name="{ column }">
                <div
                    class="d-flex align-center justify-start flex-nowrap table-header-controls"
                >
                    <span class="flex-shrink-0">{{ column.title }}</span>
                    <TableSortMenu
                        v-model="sortOrder"
                        custom-option-key="Inactive"
                        custom-option-label="Inactive"
                        @click.stop
                    />
                    <TableHeaderSearch
                        v-model="searchQuery"
                        v-model:open="searchOpen"
                    />
                </div>
            </template>

            <!-- Header for 'OT' column with tooltip -->
            <template #header.overtimeToggleEnabled="{ column }">
                <v-tooltip location="bottom" :open-delay="1000">
                    <template #activator="{ props }">
                        <span
                            v-bind="props"
                            class="ml-2"
                            style="cursor: help"
                            >{{ column.title }}</span
                        >
                    </template>
                    <span>Enable overtime for user</span>
                </v-tooltip>
            </template>

            <!-- Render Name Column as Usual -->
            <template v-slot:item.name="{ item }">
                <div class="d-flex name-cell">
                    <span class="text-ellipsis">{{ item.name || "N/A" }}</span>
                    <span class="text-ellipsis">{{ item.surname || "" }}</span>
                </div>
            </template>
            <template v-slot:item.overtimeToggleEnabled="{ item }">
                <v-checkbox-btn
                    :model-value="!!item.overtimeToggleEnabled"
                    density="compact"
                    @click.stop
                    @update:model-value="setOvertimeToggle(item.uid, $event)"
                />
            </template>

            <!-- Render Project and Hours Columns Conditionally -->
            <template v-slot:item.project="{ item }">
                <template v-if="item.status === 'outOfOffice'">
                    <span colspan="2">Out Of Office</span>
                </template>
                <template v-else>
                    <span class="text-ellipsis">{{ item.project }}</span>
                </template>
            </template>

            <template v-slot:item.hours="{ item }">
                <template v-if="item.status === 'outOfOffice'"> </template>
                <template v-else>
                    <template v-if="item.startTime && !item.endTime">
                        <ElapsedTimeDisplay :startTime="item.startTime" />
                    </template>
                    <template v-else>
                        {{ calculateTotalHours(item.startTime, item.endTime) }}
                    </template>
                </template>
            </template>
            <template v-slot:item.actions="{ item }">
                <v-menu bottom :close-on-content-click="true">
                    <template #activator="{ props }">
                        <v-btn icon flat v-bind="props" @click.stop>
                            <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                    </template>

                    <v-list>
                        <v-list-item
                            v-if="canEditWorkHours"
                            @click.stop="onEditHours(item)"
                        >
                            <v-list-item-title>Edit Hours</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                            v-if="canDoEndOfDayForUser"
                            @click.stop="confirmAdminEndOfDayForUser(item)"
                        >
                            <v-list-item-title>End of Day</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>
        </v-data-table>
    </v-card>
    <v-overlay v-model="adminConfirm.show" opacity="0.5">
        <MessageSnackbar
            :message="adminConfirm.message"
            :color="'#333'"
            :timeout="5000"
            :location="'center'"
            :rounded="'shaped'"
            :show="adminConfirm.show"
            @update:show="adminConfirm.show = $event"
        >
            <div class="d-flex flex-column align-center">
                <div class="mb-4">{{ adminConfirm.message }}</div>
                <v-btn
                    color="error"
                    variant="text"
                    size="x-large"
                    @click="adminEndOfDayForUser"
                >
                    OK
                </v-btn>
            </div>
        </MessageSnackbar>
    </v-overlay>
    <MessageSnackbar
        :message="snackbar.message"
        :color="snackbar.color"
        :timeout="5000"
        :show="snackbar.show"
        @update:show="snackbar.show = $event"
    />
</template>

<script>
import { mapGetters } from "vuex";
import {
    getUsersAndProjects,
    isBelowThreshold,
    goToUserInfo,
    triggerSnackbar,
} from "@/utils/utils";
import { getDatabase, ref, update } from "firebase/database";
import ElapsedTimeDisplay from "./ElapsedTimeDisplay.vue";
import MessageSnackbar from "./MessageSnackbar.vue";
import TableSortMenu from "./TableSortMenu.vue";
import TableHeaderSearch from "./TableHeaderSearch.vue";

export default {
    components: {
        ElapsedTimeDisplay,
        MessageSnackbar,
        TableSortMenu,
        TableHeaderSearch,
    },
    data() {
        return {
            /**
             * Table headers configuration for users, projects, and hours.
             * @type {Array<Object>}
             */
            headers: [
                {
                    title: "Users",
                    value: "name",
                    sortable: true,
                    minWidth: "160px",
                },
                {
                    title: "OT",
                    value: "overtimeToggleEnabled",
                    sortable: false,
                    maxWidth: "70px",
                },
                {
                    title: "Projects",
                    value: "project",
                    sortable: true,
                    maxWidth: "100px",
                },
                { title: "Hours", value: "hours", maxWidth: "100px" },
                {
                    title: "",
                    value: "actions",
                    sortable: false,
                    maxWidth: "20px",
                },
            ],
            adminConfirm: {
                show: false,
                message: "",
                item: null,
            },
            snackbar: {
                show: false,
                message: "",
            },
            /** @type {Boolean} Determines the visibility of the dialog. */
            showDialog: false,
            /** @type {String} Email of the user to invite. */
            inviteEmail: "",
            /** @type {String} First name of the user. */
            name: "",
            /** @type {String} Surname of the user. */
            surname: "",
            /** @type {String} Role assigned to the user. */
            role: "",
            /**
             * Available roles for a user.
             * @type {Array<String>}
             */
            roles: ["operator", "admin", "teamLead"],
            /**
             * Validation rules for form fields.
             * @type {Object}
             */
            rules: {
                required: (value) => !!value || "Required.",
                email: (value) =>
                    /.+@.+\..+/.test(value) || "E-mail must be valid.",
            },
            /**
             * Current sort order: 'A-Z', 'Z-A', or 'Inactive'.
             * @type {String}
             */
            sortOrder: "A-Z",
            searchQuery: "",
            searchOpen: false,
        };
    },
    mounted() {
        if (this.$route.query?.action === "edit-hours") {
            this.isEditingTimeEntries = true;
            this.tab = "today";
        }
        // Fetch projects if the user has rights to view them
        if (this.getRights?.viewProjects?.includes("mine")) {
            this.$store.dispatch("fetchProjects");
        }
    },

    computed: {
        canEditWorkHours() {
            const viewer = this.$store.state.user; // the logged-in viewer on this device
            return viewer?.rights.editWorkHours === true;
        },
        canDoEndOfDayForUser() {
            const viewer = this.$store.state.user; // the logged-in viewer on this device
            return viewer?.rights.canDoEndOfDayForUser === true;
        },
        isNrOfItems() {
            return isBelowThreshold(this.usersWithAllProjects);
        },
        ...mapGetters([
            "getUsers",
            "getManagedProjects",
            "getRights",
            "getAllProjects",
        ]),
        /**
         * Determines if the current user can add new users.
         * @returns {Boolean} True if the user has permission to add new users.
         */
        canAddNewUser() {
            return this.getRights?.addNewUser || false;
        },

        /**
         * Checks if the current user is an admin.
         * @returns {Boolean} True if the user is an admin (can view all users).
         */
        isAdmin() {
            return this.getRights?.viewUsers === "all";
        },

        /**
         * Retrieves the list of projects based on user permissions.
         * @returns {Array<Object>} List of selected projects.
         */
        selectedProjects() {
            // Use getAllProjects for display purposes (project name lookups)
            // This allows team leads to see project names even for projects they don't manage
            return this.getAllProjects || [];
        },

        /**
         * Determines the sort-by configuration for Vuetify data table.
         * @returns {Array<Object>|undefined} Sort configuration or undefined for custom sorting.
         */
        sortByConfig() {
            // Only use Vuetify sorting for A-Z and Z-A (not for Inactive)
            if (this.sortOrder === "A-Z") {
                return [{ key: "name", order: "asc" }];
            } else if (this.sortOrder === "Z-A") {
                return [{ key: "name", order: "desc" }];
            }
            // For "Inactive", we'll handle sorting manually in usersWithAllProjects
            return [{ key: "name", order: "asc" }];
        },

        /**
         * Users + projects list (no text search). Cached unless users/sort/projects change.
         */
        usersWithAllProjectsBase() {
            const users = this.getUsers || [];
            const projects = this.selectedProjects;
            const showInactive = this.sortOrder === "Inactive" && this.isAdmin;

            let result = getUsersAndProjects(users, projects, {
                includeInactive: showInactive,
            });

            if (this.sortOrder === "Inactive") {
                result = [...result].sort((a, b) => {
                    const nameA = `${a.name || ""} ${a.surname || ""}`
                        .trim()
                        .toLowerCase();
                    const nameB = `${b.name || ""} ${b.surname || ""}`
                        .trim()
                        .toLowerCase();
                    return nameA.localeCompare(nameB);
                });
            }

            return result;
        },

        /**
         * Table items: base list filtered by header search only.
         */
        usersWithAllProjects() {
            const q = (this.searchQuery || "").trim().toLowerCase();
            const result = this.usersWithAllProjectsBase;
            if (!q) return result;
            return result.filter((user) => {
                const fullName = `${user.name || ""} ${user.surname || ""}`
                    .trim()
                    .toLowerCase();
                return fullName.includes(q);
            });
        },
    },

    methods: {
        goToUserInfo,
        confirmAdminEndOfDayForUser(item) {
            this.adminConfirm.show = true;
            this.adminConfirm.message =
                "Are you sure you want to end the current work day for this user?";
            this.adminConfirm.item = item;
        },
        async adminEndOfDayForUser() {
            try {
                const item = this.adminConfirm.item;
                if (!item) return;
                const targetUserId = item.uid; // the user shown on the row
                const entryId = item.entryId;
                const projectId = item.projectId;

                // 1) Close the open RTDB entry (mark endTime, endOfSession)
                if (entryId) {
                    await this.$store.dispatch("endTimeEntryForUser", {
                        userId: targetUserId,
                        entryId,
                        isEndOfDay: true,
                    });
                }

                // 2) Remove presence from their current project (if any)
                if (projectId) {
                    await this.$store.dispatch("addRemoveActiveUser", {
                        oldProject: { projectId },
                        newProject: null,
                        userId: targetUserId,
                        name: null,
                        surname: null,
                    });
                }

                // 3) Reset that user’s project priorities to "Normal"
                await this.$store.dispatch("resetUserProjectsPriorityForUser", {
                    userId: targetUserId,
                });

                // 4) Mark that user's status as endOfDay
                await update(ref(getDatabase(), `users/${targetUserId}`), {
                    userStatus: "endOfDay",
                });

                // Success toast (shown once, here)
                triggerSnackbar(
                    this.snackbar,
                    "End Of Day completed.",
                    "success"
                );
            } catch (e) {
                console.error("[Admin EndOfDay] Failed:", e);
                triggerSnackbar(
                    this.snackbar,
                    "Failed to complete End Of Day.",
                    "error"
                );
            } finally {
                // Close overlay + centered snackbar
                this.adminConfirm.show = false;
                this.adminConfirm.item = null;
            }
        },
        onEditHours(item) {
            this.$router.push({
                name: "UserInfo",
                params: { uid: item.uid }, // uses :uid as in your router
                query: { action: "edit-hours" }, // tells the page to open in edit mode
            });
        },
        async setOvertimeToggle(userId, enabled) {
            try {
                await this.$store.dispatch("setUserOvertimeToggleEnabled", {
                    userId,
                    enabled,
                });
                // Users list will reflect via your existing users listener
            } catch (e) {
                console.error("Failed to update overtimeToggleEnabled:", e);
            }
        },

        /**
         * Calculates total hours worked based on start and end times.
         * @param {Number} startTime - Timestamp for the start time.
         * @param {Number} endTime - Timestamp for the end time.
         * @returns {String} - Formatted string showing hours and minutes (e.g., "5h 30min").
         */
        calculateTotalHours(startTime, endTime) {
            if (!startTime || !endTime) return "0h:0min";

            const elapsedTime = endTime - startTime; // Difference in milliseconds
            const hours = Math.floor(elapsedTime / (1000 * 60 * 60));
            const minutes = Math.floor(
                (elapsedTime % (1000 * 60 * 60)) / (1000 * 60)
            );

            return `${hours}h:${minutes}min`;
        },
    },
};
</script>

<style scoped>
.table-header-controls {
    position: relative;
    overflow: visible;
}

:deep(th) {
    overflow: visible;
}

:deep(.checkbox-label-14) {
    font-size: 12px;
    color: #000000de;
    white-space: nowrap;
}

.name-cell {
    flex-wrap: wrap;
    gap: 4px;
}

:deep(.text-ellipsis) {
    display: block;
}
</style>